import { z } from "zod";
import type { ServerActionError, ServerActionResponse, ServerAction } from "../types";
import { handleServerActionError } from "./error-handler";
import { isNextNavigationError } from "../next/errors";

type InferZodOutput<T> = T extends z.ZodTypeAny ? z.output<T> : never;
type InferZodInput<T> = T extends z.ZodTypeAny ? z.input<T> : never;

type ServerActionMiddleware<Context, Input = unknown> = (params: { context: Context; input: Input }) => Promise<{ context: Context } | { error: ServerActionError }>;

import type { RedirectConfig } from "../types";

type SafeActionClientArgs<Context extends object, InputSchema extends z.ZodTypeAny | undefined, OutputSchema extends z.ZodTypeAny | undefined> = {
  middlewareFns: ServerActionMiddleware<any, any>[];
  inputSchema?: InputSchema;
  outputSchema?: OutputSchema;
  onError?: (error: unknown, context: { parsedInput?: unknown }) => ServerActionError | undefined;
  ctxType?: Context;
  redirectConfig?: string | RedirectConfig | ((result: any) => string | RedirectConfig | undefined);
};

export class ServerActionClient<Context extends object = {}, InputSchema extends z.ZodTypeAny | undefined = undefined, OutputSchema extends z.ZodTypeAny | undefined = undefined> {
  readonly #args: SafeActionClientArgs<Context, InputSchema, OutputSchema>;

  constructor(
    args: SafeActionClientArgs<Context, InputSchema, OutputSchema> = {
      middlewareFns: [],
    }
  ) {
    this.#args = args;
  }

  /**
   * Use a middleware function.
   * @param middlewareFn Middleware function
   */
  use<NextContext extends object>(
    middlewareFn: ServerActionMiddleware<
      Context & NextContext, 
      InputSchema extends z.ZodTypeAny ? z.output<InputSchema> : undefined
    >
  ) {
    return new ServerActionClient<Context & NextContext, InputSchema, OutputSchema>({
      ...this.#args,
      middlewareFns: [...this.#args.middlewareFns, middlewareFn] as any,
      ctxType: {} as Context & NextContext,
    });
  }

  /**
   * Define the input validation schema for the action.
   * @param schema Input validation schema
   */
  inputSchema<IS extends z.ZodTypeAny>(schema: IS) {
    return new ServerActionClient<Context, IS, OutputSchema>({
      ...this.#args,
      inputSchema: schema,
    });
  }

  /**
   * Define the output validation schema for the action.
   * @param schema Output validation schema
   */
  outputSchema<OS extends z.ZodTypeAny>(schema: OS) {
    return new ServerActionClient<Context, InputSchema, OS>({
      ...this.#args,
      outputSchema: schema,
    });
  }

  /**
   * Define a custom error handler for the action.
   * @param handler Error handler function
   */
  onError(handler: (error: unknown, context: { parsedInput?: unknown }) => ServerActionError | undefined) {
    return new ServerActionClient<Context, InputSchema, OutputSchema>({
      ...this.#args,
      onError: handler,
    });
  }

  /**
   * Define a redirect configuration for successful actions.
   * @param config Redirect URL, config object, or function that returns redirect config based on result
   */
  redirect<Data = any>(
    config: string | RedirectConfig | ((result: Data) => string | RedirectConfig | undefined)
  ) {
    return new ServerActionClient<Context, InputSchema, OutputSchema>({
      ...this.#args,
      redirectConfig: config,
    });
  }


  /**
   * Define the action.
   * @param serverCodeFn Code that will be executed on the server side
   */
  action<Data>(
    serverCodeFn: InputSchema extends undefined
      ? (context: Context) => Promise<Data>
      : InputSchema extends z.ZodTypeAny 
        ? (input: InferZodOutput<InputSchema>, context: Context) => Promise<Data>
        : (context: Context) => Promise<Data>
  ): ServerAction<
    InputSchema extends undefined 
      ? void 
      : InputSchema extends z.ZodTypeAny 
        ? InferZodInput<InputSchema> 
        : void, 
    OutputSchema extends z.ZodTypeAny ? InferZodOutput<OutputSchema> : Data
  > {
    return createServerAction<
      InputSchema extends undefined 
        ? void 
        : InputSchema extends z.ZodTypeAny 
          ? InferZodInput<InputSchema> 
          : void,
      OutputSchema extends z.ZodTypeAny ? InferZodOutput<OutputSchema> : Data
    >({
      inputSchema: this.#args.inputSchema,
      outputSchema: this.#args.outputSchema,
      middlewareFns: this.#args.middlewareFns,
      onError: this.#args.onError,
      serverCodeFn: serverCodeFn as any,
      redirectConfig: this.#args.redirectConfig,
    });
  }

  /**
   * Define a form action that accepts FormData.
   * @param serverCodeFn Code that will be executed on the server side
   */
  formAction<Data>(
    serverCodeFn: InputSchema extends undefined
      ? (context: Context) => Promise<Data>
      : InputSchema extends z.ZodTypeAny 
        ? (input: InferZodOutput<InputSchema>, context: Context) => Promise<Data>
        : (context: Context) => Promise<Data>
  ): (prev: ServerActionResponse<OutputSchema extends z.ZodTypeAny ? InferZodOutput<OutputSchema> : Data>, formData: FormData) => Promise<ServerActionResponse<OutputSchema extends z.ZodTypeAny ? InferZodOutput<OutputSchema> : Data>> {
    return createFormServerAction({
      inputSchema: this.#args.inputSchema,
      outputSchema: this.#args.outputSchema,
      middlewareFns: this.#args.middlewareFns,
      onError: this.#args.onError,
      serverCodeFn,
      redirectConfig: this.#args.redirectConfig,
    });
  }
}

// Internal function to create the actual server action
function createServerAction<TInput, TOutput>(config: {
  inputSchema?: z.ZodTypeAny;
  outputSchema?: z.ZodTypeAny;
  middlewareFns: ServerActionMiddleware<any, any>[];
  onError?: (error: unknown, context: { parsedInput?: unknown }) => ServerActionError | undefined;
  serverCodeFn: (...args: any[]) => Promise<any>;
  redirectConfig?: string | RedirectConfig | ((result: any) => string | RedirectConfig | undefined);
}): ServerAction<TInput, TOutput> {
  const actionFn = async (...args: TInput extends void ? [] : [input: TInput]): Promise<ServerActionResponse<TOutput>> => {
    const context: { parsedInput?: unknown } = {};
    
    // Type guard: check if we have an input schema
    const hasInputSchema = config.inputSchema !== undefined && config.inputSchema !== null;
    const input = hasInputSchema ? args[0] as TInput : undefined;

    try {
      let parsedInput: any = undefined;

      // Input validation
      if (hasInputSchema && config.inputSchema) {
        const parseResult = config.inputSchema.safeParse(input);
        if (!parseResult.success) {
          return handleServerActionError(parseResult.error, config.onError ? (err) => config.onError!(err, { parsedInput: input }) : undefined);
        }
        parsedInput = parseResult.data;
        context.parsedInput = parsedInput;
      }

      // Execute middleware chain
      let middlewareContext = {};
      for (const middleware of config.middlewareFns) {
        const middlewareInput = hasInputSchema ? parsedInput : undefined;
        const result = await middleware({ context: middlewareContext, input: middlewareInput });
        if ("error" in result) {
          return {
            success: false,
            error: result.error,
          };
        }
        middlewareContext = { ...middlewareContext, ...result.context };
      }

      // Execute the server code
      const result = hasInputSchema 
        ? await config.serverCodeFn(parsedInput, middlewareContext)
        : await config.serverCodeFn(middlewareContext);

      // Output validation
      let validatedOutput: any = result;
      if (config.outputSchema) {
        const parseResult = config.outputSchema.safeParse(result);
        if (!parseResult.success) {
          return handleServerActionError(parseResult.error);
        }
        validatedOutput = parseResult.data;
      }

      // Handle redirect config if present
      let redirectValue: string | RedirectConfig | undefined;
      if (config.redirectConfig) {
        if (typeof config.redirectConfig === 'function') {
          redirectValue = config.redirectConfig(validatedOutput);
        } else {
          redirectValue = config.redirectConfig;
        }
      }

      return {
        success: true,
        data: validatedOutput,
        ...(redirectValue && { redirect: redirectValue }),
      };
    } catch (error) {
      // Preserve Next.js navigation errors (redirect, notFound, etc.)
      if (isNextNavigationError(error)) {
        throw error;
      }
      
      // Debug logging only in development
      if (process.env.NODE_ENV === 'development') {
        console.log("[executeAction] Error:", error?.constructor?.name, error instanceof Error ? error.message : error);
      }
      
      // Use the handleServerActionError function with custom handler
      const errorHandler = config.onError ? (err: unknown) => {
        return config.onError!(err, { parsedInput: context.parsedInput });
      } : undefined;
      
      return handleServerActionError(error, errorHandler);
    }
  };

  // Add metadata for runtime type checking
  (actionFn as any).__hasInputSchema = config.inputSchema !== undefined && config.inputSchema !== null;
  
  // Return the function with correct typing based on whether input is void
  return actionFn as ServerAction<TInput, TOutput>;
}

/**
 * Safely parses JSON strings back to objects/arrays and handles FormData boolean conventions.
 * 
 * DESIGN PRINCIPLES:
 * - Parse objects and arrays (start with { or [)
 * - Parse "true"/"false" to booleans (FormData convention for checkboxes)
 * - DO NOT parse number strings ("123") to avoid ambiguity
 * - Fail gracefully on malformed JSON
 * - Maintain perfect symmetry with client-side objectToFormData
 * 
 * WHY WE PARSE BOOLEANS:
 * - HTML forms cannot send real JavaScript booleans, only strings
 * - Checkboxes commonly send "true"/"false" strings
 * - React Hook Form uses this convention
 * - This is the expected behavior in FormData context
 */
function tryParseJSON(value: unknown): unknown {
  // Non-strings pass through unchanged
  if (typeof value !== 'string') return value;
  
  const trimmed = value.trim();
  
  // Empty strings remain empty
  if (!trimmed) return value;
  
  // Parse boolean strings (common for checkboxes and React Hook Form)
  // This is safe in FormData context since HTML can't send real booleans
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  
  // Parse null (less common but sometimes used)
  if (trimmed === 'null') return null;
  
  // Only attempt parsing for obvious JSON structures
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    try {
      const parsed = JSON.parse(trimmed);
      // Extra safety: verify we got an object/array
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed;
      }
    } catch {
      // Malformed JSON - return original string
    }
  }
  
  // All other strings (including number strings like "123") remain as strings
  // This avoids ambiguity with actual string values that happen to be numeric
  return value;
}

// Form data parser with automatic JSON deserialization
function parseFormData(formData: FormData): Record<string, any> {
  const result: Record<string, any> = {};
  const keys = new Set<string>();

  for (const [key] of formData.entries()) {
    keys.add(key);
  }

  for (const key of keys) {
    const values = formData.getAll(key);

    if (key.endsWith("[]")) {
      const cleanKey = key.slice(0, -2);
      // Parse each array item (handles nested objects in arrays)
      result[cleanKey] = values.map(tryParseJSON);
    } else if (values.length === 1) {
      // Parse single value (handles JSON stringified objects)
      result[key] = tryParseJSON(values[0]);
    } else {
      // Parse multiple values
      result[key] = values.map(tryParseJSON);
    }
  }

  return result;
}

// Internal function to create form server action
function createFormServerAction<TOutput>(config: {
  inputSchema?: z.ZodTypeAny;
  outputSchema?: z.ZodTypeAny;
  middlewareFns: ServerActionMiddleware<any>[];
  onError?: (error: unknown, context: { parsedInput?: unknown }) => ServerActionError | undefined;
  serverCodeFn: (...args: any[]) => Promise<any>;
  redirectConfig?: string | RedirectConfig | ((result: any) => string | RedirectConfig | undefined);
}): (prev: ServerActionResponse<TOutput>, formData: FormData) => Promise<ServerActionResponse<TOutput>> {
  return async (_prev: ServerActionResponse<TOutput>, formData: FormData): Promise<ServerActionResponse<TOutput>> => {
    const context: { parsedInput?: unknown } = {};
    const hasInputSchema = config.inputSchema !== undefined && config.inputSchema !== null;

    try {
      let parsedInput: any = parseFormData(formData);

      // Input validation
      if (config.inputSchema) {
        const parseResult = config.inputSchema.safeParse(parsedInput);
        if (!parseResult.success) {
          return handleServerActionError(parseResult.error, config.onError ? (err) => config.onError!(err, { parsedInput }) : undefined);
        }
        parsedInput = parseResult.data;
        context.parsedInput = parsedInput;
      }

      // Execute middleware chain
      let middlewareContext = {};
      for (const middleware of config.middlewareFns) {
        const middlewareInput = hasInputSchema ? parsedInput : undefined;
        const result = await middleware({ context: middlewareContext, input: middlewareInput });
        if ("error" in result) {
          return {
            success: false,
            error: result.error,
          };
        }
        middlewareContext = { ...middlewareContext, ...result.context };
      }

      // Execute the server code
      const result = hasInputSchema 
        ? await config.serverCodeFn(parsedInput, middlewareContext)
        : await config.serverCodeFn(middlewareContext);

      // Output validation
      let validatedOutput: any = result;
      if (config.outputSchema) {
        const parseResult = config.outputSchema.safeParse(result);
        if (!parseResult.success) {
          return handleServerActionError(parseResult.error);
        }
        validatedOutput = parseResult.data;
      }

      // Handle redirect config if present
      let redirectValue: string | RedirectConfig | undefined;
      if (config.redirectConfig) {
        if (typeof config.redirectConfig === 'function') {
          redirectValue = config.redirectConfig(validatedOutput);
        } else {
          redirectValue = config.redirectConfig;
        }
      }

      return {
        success: true,
        data: validatedOutput,
        ...(redirectValue && { redirect: redirectValue }),
      };
    } catch (error) {
      // Preserve Next.js navigation errors (redirect, notFound, etc.)
      if (isNextNavigationError(error)) {
        throw error;
      }
      // Debug logging only in development
      if (process.env.NODE_ENV === 'development') {
        console.log("[ServerActionClient] Error:", error?.constructor?.name, error instanceof Error ? error.message : error);
      }
      const errorHandler = config.onError ? (err: unknown) => {
        return config.onError!(err, { parsedInput: context.parsedInput });
      } : undefined;
      return handleServerActionError(error, errorHandler);
    }
  };
}

// Export factory function for creating a new client
export function createActionClient() {
  return new ServerActionClient();
}
